import { useQuery } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { asRows } from '@shared/api/envelope';
import { key, STALE_TIME } from '@shared/api/query-keys';

import type { DocumentRow, EncodeJob, MediaAsset, MediaAssetBytes, Rendition } from '../_shared/types';

/**
 * The reads the assets surface performs.
 *
 * **The ladder and the documents are read per asset, and only once their
 * disclosure is opened.** Nine assets × four rungs is thirty-six rows that would
 * bury the asset list, which is why the block is folded — and fetching them all
 * on arrival would spend nine requests on rows nobody has asked to see. The
 * query is enabled by the disclosure rather than by the page.
 */

export function mediaAssetsKey(contentItemId: string) {
  return key('assets', 'mediaAssets', contentItemId);
}

/**
 * The **byte half** of an item's assets, and only that.
 *
 * This service names its columns nothing like the owning one: the kind is
 * `assetKind`, the states are `storageState` / `encodeState`, and there is no
 * title and no ordinal at all — deliberately, since `content` rewrites the
 * ordinal on every removal. The console read this payload as though it were
 * `content`'s row, so `kind` was `undefined` on every asset: the trailer and the
 * film file were never found, the page said the title held nothing, and the
 * operator's next upload was refused as a conflict against a row nothing on
 * screen had mentioned. Translated once, here, for the same reason the upload
 * policy is.
 */
export function useMediaAssets(contentItemId: string | undefined) {
  return useQuery({
    queryKey: mediaAssetsKey(contentItemId ?? ''),
    // **A page, not a bare array.** The route answers `{items, page_size, total,
    // has_next, next_cursor}`; read as an array it was `undefined.find(...)`,
    // which took the whole assets page down on a 200.
    queryFn: async (): Promise<MediaAssetBytes[]> =>
      asRows<WireMediaAssetBytes>(await api.get('assets', `/contentItems/${contentItemId}/mediaAssets`)).map(
        (row) => ({
          id: row.id,
          group_id: row.groupId ?? null,
          kind: row.assetKind,
          is_free_preview: Boolean(row.isFreePreview),
          storage_state: row.storageState,
          encode_state: row.encodeState,
          // Milliseconds here, seconds in `content`. Converted at the seam rather
          // than at the two call sites that would otherwise each have to remember.
          duration_seconds: row.durationMs === null ? null : Math.round(row.durationMs / 1000),
        }),
      ),
    enabled: Boolean(contentItemId),
    staleTime: STALE_TIME.detail,
  });
}

interface WireMediaAssetBytes {
  id: string;
  groupId: string | null;
  assetKind: string;
  isFreePreview: boolean;
  storageState: string;
  encodeState: string;
  durationMs: number | null;
}

/**
 * Put the byte half onto the record half, by id.
 *
 * `content` decides which assets exist and in what order, so its list is the one
 * iterated; an id `assets` holds and `content` does not is a row whose record was
 * removed while its bytes are still being collected, and drawing it would offer
 * an operator controls over something that no longer exists.
 */
export function joinAssetBytes(records: MediaAsset[], bytes: MediaAssetBytes[]): MediaAsset[] {
  const byId = new Map(bytes.map((row) => [row.id, row]));

  return records.map((record) => {
    const byteRow = byId.get(record.id);
    if (!byteRow) return record;
    return {
      ...record,
      is_free_preview: byteRow.is_free_preview,
      storage_state: byteRow.storage_state,
      encode_state: byteRow.encode_state,
      duration_seconds: record.duration_seconds ?? byteRow.duration_seconds,
    };
  });
}

export function renditionsKey(mediaAssetId: string) {
  return key('assets', 'renditions', mediaAssetId);
}

/**
 * The four rungs of one asset. **No signed URL and no object key** comes back on
 * any role: this read answers «does the media exist», never «let me watch it».
 */
export function useRenditions(mediaAssetId: string, enabled: boolean) {
  return useQuery({
    queryKey: renditionsKey(mediaAssetId),
    // **Translated at the seam, like the byte half above.** This service names
    // the column `sizeBytes`; the console's row says `size_bytes`, so every
    // rung read `undefined` and the size column printed «ليس رقمًا بايت» — on
    // the ready rungs too, not only the ones with no file.
    queryFn: async (): Promise<Rendition[]> =>
      asRows<WireRendition>(await api.get('assets', `/mediaAssets/${mediaAssetId}/renditions`))
        .map((row) => ({
          id: row.id,
          label: row.label,
          height: row.height,
          // A rung that was never encoded reports `0`, which is not a size — it
          // is the absence of a file, and the column already draws «—» for that.
          size_bytes: typeof row.sizeBytes === 'number' && row.sizeBytes > 0 ? row.sizeBytes : null,
          state: row.state,
        }))
        // **The ladder is ordered here, not hoped for.** The rungs were drawn in
        // whatever order the payload arrived in — «٤٨٠، ٢١٦٠، ١٠٨٠، ٧٢٠» on the
        // develop asset this was first seen on — and a ladder is a ladder
        // because its rungs are in order. Reading correctly today is a property
        // of today's data, not of this surface; sorting makes it a property of
        // the surface. Descending, because the top rung is the one the
        // configuration names first and the one an operator checks first.
        .sort((a, b) => rungHeight(b) - rungHeight(a)),
    enabled,
    staleTime: STALE_TIME.list,
  });
}

/**
 * How tall a rung is, for ordering.
 *
 * `height` is the figure when the service carries one; the label is the
 * fallback, because a rung that was skipped is still a rung on the ladder and
 * still has a place in the order — it just has no encoded file behind it to
 * measure. A label that is not a number sorts to the bottom rather than
 * throwing the rest of the table out of order.
 */
function rungHeight(rung: { height: number | null; label: string }): number {
  if (typeof rung.height === 'number' && rung.height > 0) return rung.height;
  const fromLabel = Number.parseInt(rung.label, 10);
  return Number.isFinite(fromLabel) ? fromLabel : 0;
}

interface WireRendition {
  id: string;
  label: string;
  width: number | null;
  height: number | null;
  bitrateKbps: number | null;
  deliveryMode: string;
  sizeBytes: number | null;
  state: string;
}

export function encodeJobsKey(mediaAssetId: string) {
  return key('assets', 'encodeJobs', mediaAssetId);
}

/**
 * The encode attempts behind one asset's ladder.
 *
 * **A failed encode was invisible in this console.** The rung table draws one
 * row per rendition and a rung that was never produced simply reads `SKIPPED`,
 * so an asset whose whole job failed looked like four deliberate skips: no
 * failure badge, no reason, and no way to ask for another attempt. The owning
 * service has always had all three — `state`, `errorCode` / `errorDetail`, and
 * `POST /encodeJobs/{id}:retry` — and none of them had a reader.
 *
 * `requestedLadder` is the job's own record of what was asked for and what
 * became of each rung, which is the only place a `SKIPPED` rung can be told
 * apart from one that was never requested.
 */
export function useEncodeJobs(mediaAssetId: string, enabled: boolean) {
  return useQuery({
    queryKey: encodeJobsKey(mediaAssetId),
    queryFn: async (): Promise<EncodeJob[]> =>
      asRows<WireEncodeJob>(await api.get('assets', `/mediaAssets/${mediaAssetId}/encodeJobs`)).map(
        (row) => ({
          id: row.id,
          state: row.state,
          requested_ladder: normalizeLadder(row.requestedLadder, row.state),
          attempt_count: row.attemptCount,
          error_code: row.errorCode,
          error_detail: row.errorDetail,
          finished_at: row.finishedAt,
        }),
      ),
    enabled,
    staleTime: STALE_TIME.list,
  });
}

/**
 * `requestedLadder` takes **two shapes on the wire**, and the console met the
 * second one live.
 *
 * A finished job states a map of rung to what became of it —
 * `{"480": "SKIPPED", …}`. A job that is still queued or running has no
 * outcomes yet, so it states the bare list of rungs it asked for —
 * `["2160", "1080", "720", "480"]`. Read as a map, that list produced its own
 * array indices as rung labels: the table drew «٠ ٢١٦٠» and «١ ١٠٨٠» — an index
 * glued to a resolution, which is exactly the kind of figure an operator reads
 * as data.
 *
 * The list form carries the job's own state as each rung's outcome, because
 * that is what it means: every rung is in whatever the job is in.
 */
function normalizeLadder(
  ladder: Record<string, string> | string[] | null,
  jobState: string,
): readonly (readonly [string, string])[] {
  if (!ladder) return [];
  // **Pairs, not a record**, so the rungs render in the order the service
  // stated them. A record re-sorts integer-like keys ascending whatever order
  // they arrived in, which would silently reverse the descending ladder the
  // in-flight form sends.
  if (Array.isArray(ladder)) return ladder.map((rung) => [rung, jobState] as const);
  return Object.entries(ladder).map(([rung, outcome]) => [rung, outcome] as const);
}

interface WireEncodeJob {
  id: string;
  mediaAssetId: string;
  state: string;
  requestedLadder: Record<string, string> | string[] | null;
  attemptCount: number;
  errorCode: string | null;
  errorDetail: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

export function documentsKey(subjectType: string, subjectId: string) {
  return key('assets', 'documents', subjectType, subjectId);
}

/**
 * The documents attached to one subject.
 *
 * The collection is **flat and carries its subject**, because the parent is
 * polymorphic across three levels — an asset, a season, or the title itself.
 * Three sub-resource routes would be three filter schemas for one behaviour, and
 * the attachment level is data the operator chooses rather than a different
 * operation.
 */
export function useDocuments(subjectType: string, subjectId: string, enabled: boolean) {
  return useQuery({
    queryKey: documentsKey(subjectType, subjectId),
    queryFn: () =>
      api.get<DocumentRow[]>('assets', `/documents?subjectType=${subjectType}&subjectId=${subjectId}`),
    enabled,
    staleTime: STALE_TIME.list,
  });
}
